/**
 * /lifecycle/hard-delete — Operator queue page (OCP-82).
 *
 * Four tables:
 *   - Deletion overdue (OCP-850) - the export window ended more than a
 *     day ago and the tenant is still not deleted. Shown first: the AVV
 *     obliges the deletion, and this is the state that went unnoticed
 *     while an unwired archive step blocked every cron run.
 *   - Provider cleanup outstanding (OCP-547) - deletes that have NOT
 *     finished at the provider. Shown FIRST because a row here means a
 *     tenant we have reported as deleted may still hold a live bucket,
 *     access key, stack or gateway key. The 15-minute resume sweep
 *     retries them automatically; this table is how an operator notices
 *     one that is not healing.
 *   - Eligible NOW (sweep would enqueue these on next run)
 *   - Upcoming within 7 days
 *
 * Per row: tenant link, days paused, and an inline "Abort" /
 * "Trigger now" button per the operator's discretion. The page
 * auto-refreshes every 60 s.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';

interface QueueRow {
  id: string;
  subdomain: string;
  paused_at: string;
  days_paused: number;
}

/** OCP-547 - a delete whose provider cleanup has not confirmed. */
interface OutstandingPurge {
  id: string;
  subdomain: string;
  origin: 'hard_delete' | 'rollback';
  attempts: number;
  pending_steps: string[];
  last_error: string | null;
  first_failed_at: string | null;
  claimed_at: string | null;
  resumable: boolean;
}

/** OCP-850 - export window ended more than a day ago, still not deleted. */
interface OverdueRow {
  id: string;
  subdomain: string;
  paused_at: string;
  window_ended_at: string;
  days_overdue: number;
  deletion_state: string | null;
  purge_last_error: string | null;
}

interface QueueResponse {
  eligible: QueueRow[];
  upcoming: QueueRow[];
  outstanding_purges?: OutstandingPurge[];
  overdue?: OverdueRow[];
}

const STEP_LABEL: Record<string, string> = {
  coolify: 'Coolify service',
  rustfs: 'storage bucket + key',
  cloudflare: 'DNS records',
  litellm: 'managed-AI key',
  oms_mail: 'instance mail key',
  backups: 'managed backups',
  file_backups: 'file backup snapshots',
  training_data: 'training data',
  backup_key: 'backup key',
};

const POLL_INTERVAL_MS = 60_000;

export function AdminHardDeleteQueuePage() {
  const [data, setData] = useState<QueueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<QueueResponse>('/v1/admin/lifecycle/hard-delete/queue');
      setData(r);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load queue.');
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const onAbort = useCallback(
    async (id: string) => {
      if (!confirm('Reset the 90-day grace for this tenant? (Keeps it paused, but pushes the hard-delete out another 90 days.)')) return;
      setPendingId(id);
      try {
        await api(`/v1/admin/tenants/${id}/hard-delete/abort`, {
          method: 'POST',
          body: { mode: 'reset_grace' },
        });
        void load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Abort failed.');
      } finally {
        setPendingId(null);
      }
    },
    [load],
  );

  const onTriggerNow = useCallback(
    async (id: string, subdomain: string) => {
      const confirmName = prompt(
        `Type the tenant subdomain ("${subdomain}") to confirm an immediate hard-delete:`,
      );
      if (confirmName !== subdomain) return;
      setPendingId(id);
      try {
        await api(`/v1/admin/tenants/${id}/hard-delete`, {
          method: 'POST',
          body: { skip_grace_check: true },
        });
        void load();
      } catch (err) {
        const code =
          err instanceof ApiError &&
          err.body &&
          typeof err.body === 'object' &&
          'error' in (err.body as Record<string, unknown>)
            ? ((err.body as Record<string, unknown>).error as string)
            : null;
        // OCP-547 - a provider did not confirm: the row is parked in purging
        // and the resume sweep retries; it shows up in the table above.
        if (code === 'purge_incomplete') {
          setError('Provider cleanup did not confirm for every resource - the tenant is parked in purging and the 15-minute resume sweep retries it.');
        } else {
          setError(err instanceof Error ? err.message : 'Trigger failed.');
        }
      } finally {
        setPendingId(null);
      }
    },
    [load],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Hard-delete queue</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Tenants whose contract has ended (<code>subscription_state=paused</code>)
          past the AGB V4 12.3 export window. The daily sweep at 03:00 UTC
          deletes them: no archive and no copy is kept (AVV section 7); each
          deletion writes a deletion record, shown on the tenant page.
          Auto-refreshes every 60 s.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!data && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {data && (data.overdue?.length ?? 0) > 0 && (
        <Section title={`Deletion overdue (${data.overdue!.length})`} testid="overdue-deletions">
          <div className="mb-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-900 dark:text-red-200">
            The export window of these tenants ended more than a day ago and
            they are still not deleted. The AVV obliges the deletion after the
            end of the contract; each one has raised a critical operator
            notification. Fix the cause (job error, or the provider named in
            the purge error) and delete the tenant with Trigger now.
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Tenant</th>
                  <th className="px-3 py-2 text-left">Window ended</th>
                  <th className="px-3 py-2 text-right">Days overdue</th>
                  <th className="px-3 py-2 text-left">State</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {data.overdue!.map((o) => (
                  <tr key={o.id} data-testid={`overdue-row-${o.id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/tenants/${o.id}`} className="text-brand-700 underline">
                        {o.subdomain}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{formatDate(o.window_ended_at)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700 dark:text-red-300">{o.days_overdue}</td>
                    <td className="max-w-md truncate px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                      {o.deletion_state === 'purging'
                        ? `purging${o.purge_last_error ? ` - ${o.purge_last_error}` : ''}`
                        : 'not claimed'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {o.deletion_state !== 'purging' && (
                        <button
                          type="button"
                          onClick={() => onTriggerNow(o.id, o.subdomain)}
                          disabled={pendingId === o.id}
                          className="text-xs text-red-700 dark:text-red-300 underline disabled:cursor-not-allowed disabled:text-gray-400"
                        >
                          Trigger now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {data && (data.outstanding_purges?.length ?? 0) > 0 && (
        <Section
          title={`Provider cleanup outstanding (${data.outstanding_purges!.length})`}
          testid="outstanding-purges"
        >
          <div className="mb-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-900 dark:text-red-200">
            These tenants are in <code>deletion_state=purging</code>: the row and
            every identifier are deliberately kept until the provider confirms.
            Until a row clears, the tenant may still hold live storage, a live
            key or a live stack - so the deletion is <strong>not</strong> done
            and must not be reported as done. The resume sweep retries every
            15 minutes; a row that keeps failing needs the provider looked at.
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Tenant</th>
                  <th className="px-3 py-2 text-left">Origin</th>
                  <th className="px-3 py-2 text-left">Still outstanding</th>
                  <th className="px-3 py-2 text-right">Attempts</th>
                  <th className="px-3 py-2 text-left">Since</th>
                  <th className="px-3 py-2 text-left">Last error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {data.outstanding_purges!.map((p) => (
                  <tr key={p.id} data-testid={`purge-row-${p.id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/tenants/${p.id}`} className="text-brand-700 underline">
                        {p.subdomain}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {p.origin === 'rollback' ? 'rollback' : 'hard delete'}
                    </td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      {p.pending_steps.length === 0
                        ? 'claimed, running now'
                        : p.pending_steps.map((s) => STEP_LABEL[s] ?? s).join(', ')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.attempts}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {p.first_failed_at ? formatDate(p.first_failed_at) : '-'}
                      {!p.resumable && (
                        <span className="ml-1 text-xs text-gray-500">(in progress)</span>
                      )}
                    </td>
                    <td className="max-w-md truncate px-3 py-2 text-xs text-red-700 dark:text-red-300">
                      {p.last_error ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {data && (
        <>
          <Section title={`Eligible now (${data.eligible.length})`} testid="eligible-table">
            {data.eligible.length === 0 ? (
              <Empty>No tenants currently eligible — the daily sweep will be a no-op.</Empty>
            ) : (
              <QueueTable
                rows={data.eligible}
                pendingId={pendingId}
                onAbort={onAbort}
                onTriggerNow={onTriggerNow}
                actionLabel="Trigger now"
                showTriggerNow
              />
            )}
          </Section>
          <Section title={`Upcoming within 7 days (${data.upcoming.length})`} testid="upcoming-table">
            {data.upcoming.length === 0 ? (
              <Empty>No tenants approaching the grace boundary in the next 7 days.</Empty>
            ) : (
              <QueueTable
                rows={data.upcoming}
                pendingId={pendingId}
                onAbort={onAbort}
                onTriggerNow={onTriggerNow}
                actionLabel="Trigger now"
                showTriggerNow={false}
              />
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({
  title,
  testid,
  children,
}: {
  title: string;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <section data-testid={testid}>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
      {children}
    </div>
  );
}

function QueueTable({
  rows,
  pendingId,
  onAbort,
  onTriggerNow,
  showTriggerNow,
}: {
  rows: QueueRow[];
  pendingId: string | null;
  onAbort: (id: string) => void;
  onTriggerNow: (id: string, subdomain: string) => void;
  actionLabel: string;
  showTriggerNow: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left">Tenant</th>
            <th className="px-3 py-2 text-left">Paused at</th>
            <th className="px-3 py-2 text-right">Days paused</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`queue-row-${r.id}`}>
              <td className="px-3 py-2">
                <Link to={`/tenants/${r.id}`} className="text-brand-700 underline">
                  {r.subdomain}
                </Link>
              </td>
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{formatDate(r.paused_at)}</td>
              <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{r.days_paused}</td>
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  onClick={() => onAbort(r.id)}
                  disabled={pendingId === r.id}
                  className="mr-2 text-xs text-brand-700 underline disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  Reset grace
                </button>
                {showTriggerNow && (
                  <button
                    type="button"
                    onClick={() => onTriggerNow(r.id, r.subdomain)}
                    disabled={pendingId === r.id}
                    className="text-xs text-red-700 dark:text-red-300 underline disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    Trigger now
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
